import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Loader2, Search, FileText, ArrowLeft } from "lucide-react";

const STATUS_LABEL = {
  under_review: "New / Under Review", resubmitted: "Resubmitted / Ready for Review",
  more_info: "More Information Needed", approved: "Approved",
  evaluation_in_progress: "Evaluation In Progress", evaluation_completed: "Evaluation Completed",
  not_selected: "Not Selected",
};

export default function Admin() {
  const navigate = useNavigate();
  const { loading } = useAuth();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [tab, setTab] = useState("apps");
  const [waitlist, setWaitlist] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const loadWaitlist = useCallback(async () => {
    try { const res = await api.get("/admin/waitlist"); setWaitlist(res.data.waitlist); } catch {}
  }, []);
  const loadPilots = useCallback(async () => {
    try { const res = await api.get("/admin/pilots"); setPilots(res.data.pilots); } catch {}
  }, []);
  const loadAnalytics = useCallback(async () => {
    try { const res = await api.get("/admin/eval/analytics"); setAnalytics(res.data); } catch {}
  }, []);
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/eval/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "fiad_pilot.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/admin/files/${fileId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the file."); }
  };

  const loadList = useCallback(async () => {
    try {
      const res = await api.get("/admin/applications", { params: { q, status: statusFilter } });
      setApps(res.data.applications);
    } catch (e) { if (e?.response?.status === 403 || e?.response?.status === 401) { /* handled below */ } }
  }, [q, statusFilter]);

  useEffect(() => {
    (async () => {
      try { const res = await api.get("/auth/me"); setMe(res.data); if (!res.data.is_admin) return; }
      catch { setMe(null); }
      finally { setChecking(false); }
    })();
  }, []);

  useEffect(() => { if (me?.is_admin) loadList(); }, [me, loadList]);
  useEffect(() => { if (!checking && !me) navigate("/admin/login"); }, [checking, me, navigate]);
  useEffect(() => { if (me?.is_admin) loadWaitlist(); }, [me, loadWaitlist]);
  useEffect(() => { if (me?.is_admin) loadPilots(); }, [me, loadPilots]);
  useEffect(() => { if (me?.is_admin && tab === "analytics") loadAnalytics(); }, [me, tab, loadAnalytics]);

  const openDetail = async (id) => {
    try { const res = await api.get(`/admin/applications/${id}`); setSelected(res.data); setMessage(""); }
    catch { toast.error("Could not load application."); }
  };

  const act = async (path, body) => {
    try {
      const res = await api.post(`/admin/applications/${selected.application.id}/${path}`, body || {});
      toast.success("Updated.");
      setSelected((s) => ({ ...s, application: res.data.application }));
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };

  if (loading || checking) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (!me) return null;
  if (!me.is_admin) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-[#F6F3EC] gap-3" data-testid="admin-denied">
      <ShieldCheck className="h-8 w-8 text-[#525252]" />
      <p className="text-sm text-[#525252]">Administrator access only.</p>
      <button onClick={() => navigate("/admin/login")} className="text-sm underline">Go to admin sign in</button>
    </div>;
  }

  const a = selected?.application;
  return (
    <div className="min-h-screen bg-[#F6F3EC]">
      <header className="w-full border-b border-[#E5E5E5] bg-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><img src="/fiad-mark-dark.png" alt="FIAD" className="h-7 w-auto select-none" draggable="false" /><span className="font-display text-xl font-bold tracking-tight">FIAD Admin</span></div>
          <button onClick={() => navigate("/dashboard")} className="text-sm text-[#525252] hover:text-[#0A0A0A]">Dashboard</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {!a ? (
          <>
            <div className="flex items-center gap-6 border-b border-[#E5E5E5] mb-6">
              <button data-testid="tab-apps" onClick={() => setTab("apps")} className={`pb-3 text-sm font-medium ${tab === "apps" ? "text-[#0A0A0A] border-b-2 border-[#0A0A0A]" : "text-[#A3A3A3]"}`}>Applications</button>
              <button data-testid="tab-waitlist" onClick={() => setTab("waitlist")} className={`pb-3 text-sm font-medium ${tab === "waitlist" ? "text-[#0A0A0A] border-b-2 border-[#0A0A0A]" : "text-[#A3A3A3]"}`}>Waitlist</button>
              <button data-testid="tab-pilots" onClick={() => setTab("pilots")} className={`pb-3 text-sm font-medium ${tab === "pilots" ? "text-[#0A0A0A] border-b-2 border-[#0A0A0A]" : "text-[#A3A3A3]"}`}>AI Lab Pilots</button>
              <button data-testid="tab-analytics" onClick={() => setTab("analytics")} className={`pb-3 text-sm font-medium ${tab === "analytics" ? "text-[#0A0A0A] border-b-2 border-[#0A0A0A]" : "text-[#A3A3A3]"}`}>Evaluation Analytics</button>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{tab === "apps" ? "Applications" : tab === "waitlist" ? "Waitlist" : tab === "pilots" ? "AI Lab Pilot Requests" : "Evaluation Analytics"}</h1>
            <div className={`mt-6 flex-col sm:flex-row gap-3 ${tab === "apps" ? "flex" : "hidden"}`}>
              <div className="flex items-center gap-2 border border-[#E5E5E5] rounded-sm px-3 py-2 bg-white flex-1">
                <Search className="h-4 w-4 text-[#A3A3A3]" />
                <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="text-sm w-full focus:outline-none" />
              </div>
              <select data-testid="admin-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm bg-white">
                <option value="">All statuses</option>
                {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>

            <div className={`mt-6 bg-white border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5] ${tab === "apps" ? "" : "hidden"}`} data-testid="admin-app-list">
              {apps.length === 0 && <div className="p-6 text-sm text-[#A3A3A3]">No applications found.</div>}
              {apps.map((ap) => (
                <button key={ap.id} data-testid={`admin-app-${ap.id}`} onClick={() => openDetail(ap.id)} className="w-full text-left p-4 hover:bg-[#F5F5F5] flex items-center justify-between gap-4">
                  <div><p className="font-medium">{ap.full_name || "—"}</p><p className="text-sm text-[#525252]">{ap.email}</p></div>
                  <div className="text-right"><span className="text-xs font-medium uppercase tracking-wider text-[#026AA2]">{STATUS_LABEL[ap.status] || ap.status}</span><p className="text-xs text-[#A3A3A3]">{(ap.submission_date || "").slice(0, 10)}</p></div>
                </button>
              ))}
            </div>
            {tab === "waitlist" && (
              <div className="mt-6 bg-white border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]" data-testid="admin-waitlist-list">
                {waitlist.length === 0 && <div className="p-6 text-sm text-[#A3A3A3]">No waitlist entries yet.</div>}
                {waitlist.map((w) => (
                  <div key={w.id} data-testid={`waitlist-row-${w.id}`} className="p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-medium">{w.name} <span className="text-xs text-[#A3A3A3]">· {w.domain}</span></p>
                      <p className="text-sm text-[#525252]">{w.email} · {w.phone || "no phone"}</p>
                      {w.linkedin && <a href={w.linkedin} target="_blank" rel="noreferrer" className="text-xs text-[#026AA2] underline">LinkedIn</a>}
                    </div>
                    <div className="flex items-center gap-4">
                      {w.resume?.file_id && <button onClick={() => downloadFile(w.resume.file_id, w.resume.filename)} className="text-sm text-[#026AA2] underline">Resume</button>}
                      <span className="text-xs text-[#A3A3A3]">{(w.created_at || "").slice(0, 10)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab === "pilots" && (
              <div className="mt-6 bg-white border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]" data-testid="admin-pilots-list">
                {pilots.length === 0 && <div className="p-6 text-sm text-[#A3A3A3]">No pilot requests yet.</div>}
                {pilots.map((p) => (
                  <div key={p.id} data-testid={`pilot-row-${p.id}`} className="p-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <p className="font-medium">{p.company} <span className="text-xs text-[#A3A3A3]">· {p.name}</span></p>
                        <p className="text-sm text-[#525252]"><a href={`mailto:${p.work_email}`} className="text-[#026AA2] underline">{p.work_email}</a></p>
                      </div>
                      <span className="text-xs text-[#A3A3A3]">{(p.created_at || "").slice(0, 10)}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#525252]">
                      {p.evaluation_domain && <span>Domain: {p.evaluation_domain}</span>}
                      {p.languages && <span>Languages: {p.languages}</span>}
                      {p.project_scale && <span>Scale: {p.project_scale}</span>}
                      {p.timeline && <span>Timeline: {p.timeline}</span>}
                    </div>
                    {p.message && <p className="mt-2 text-sm text-[#525252] whitespace-pre-wrap">{p.message}</p>}
                  </div>
                ))}
              </div>
            )}
            {tab === "analytics" && (
              <div className="mt-6 space-y-6" data-testid="admin-analytics">
                <div className="flex justify-end">
                  <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#262626]">Export Pilot CSV</button>
                </div>
                {analytics && (<>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(analytics.counts).map(([k, v]) => (
                      <div key={k} className="bg-white border border-[#E5E5E5] rounded-sm p-4">
                        <p className="font-mono-num text-2xl">{v}</p>
                        <p className="text-xs text-[#A3A3A3] mt-1">{k.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white border border-[#E5E5E5] rounded-sm p-5">
                    <p className="eyebrow text-[#A3A3A3]">Cohort</p>
                    <div className="mt-3 flex flex-wrap gap-x-10 gap-y-2 text-sm">
                      <span>N = <strong>{analytics.cohort.n}</strong></span>
                      <span>Pre-Test mean: <strong>{analytics.cohort.pretest_mean ?? "—"}%</strong></span>
                      <span>Vetting mean: <strong>{analytics.cohort.vetting_mean ?? "—"}%</strong></span>
                      <span data-testid="observed-delta">Observed Δ alignment: <strong>{analytics.cohort.observed_delta ?? "—"}</strong></span>
                    </div>
                    <p className="mt-2 text-xs text-[#A3A3A3]">{analytics.cohort.delta_label}</p>
                  </div>
                  {analytics.item_heat.length > 0 && (
                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-5">
                      <p className="eyebrow text-[#A3A3A3] mb-3">Item heat (most missed)</p>
                      <div className="flex flex-wrap gap-2">
                        {analytics.item_heat.map((h) => (<span key={h.item_id} className="text-xs bg-[#F6F3EC] border border-[#E5E5E5] px-2 py-1 rounded-sm">{h.item_id} · {h.missed}</span>))}
                      </div>
                    </div>
                  )}
                  <div className="bg-white border border-[#E5E5E5] rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-[#A3A3A3] border-b border-[#E5E5E5]">
                        {["Name", "Email", "Pseudonym", "State", "Pre %", "Vet %", "Retest %", "Integrity", "Suspicious", "Alpha", "Attempt"].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}
                      </tr></thead>
                      <tbody data-testid="analytics-people">
                        {analytics.people.map((p) => (
                          <tr key={p.user_id} className="border-b border-[#F0F0F0]">
                            <td className="px-3 py-2 whitespace-nowrap font-medium" data-testid={`person-name-${p.user_id}`}>{p.full_name || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-[#525252]" data-testid={`person-email-${p.user_id}`}>{p.email || "—"}</td>
                            <td className="px-3 py-2 font-mono-num text-xs">{p.pseudonym}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{p.state}</td>
                            <td className="px-3 py-2">{p.pretest_pct ?? "—"}</td>
                            <td className="px-3 py-2">{p.vetting_pct ?? "—"}</td>
                            <td className="px-3 py-2">{p.retest_pct ?? "—"}</td>
                            <td className="px-3 py-2">{p.integrity_flag || "—"}</td>
                            <td className="px-3 py-2">{p.suspicious_rationales ? p.suspicious_rationales : "—"}</td>
                            <td className="px-3 py-2">{p.alpha_completed ? "yes" : "no"}</td>
                            <td className="px-3 py-2">{p.attempt}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-[#A3A3A3]">Names and emails are shown to authorised admins only. The exported Pilot CSV stays pseudonymised (participant_pseudo).</p>
                </>)}
              </div>
            )}
          </>
        ) : (
          <div data-testid="admin-detail">
            <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] mb-6"><ArrowLeft className="h-4 w-4" /> Back to list</button>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-sm p-6 space-y-4">
                <div className="flex items-center justify-between"><h1 className="font-display text-2xl font-bold tracking-tight">{a.full_name || "—"}</h1><span data-testid="detail-status" className="text-xs font-medium uppercase tracking-wider text-[#026AA2]">{STATUS_LABEL[a.status] || a.status}</span></div>
                <p className="text-sm text-[#525252]">Submitted {(a.submission_date || "").slice(0, 10)}</p>

                <div><span className="text-xs font-medium uppercase tracking-wider text-[#525252]">Candidate profile</span>
                  <dl data-testid="candidate-profile" className="mt-2 text-sm divide-y divide-[#E5E5E5]">
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Email</dt><dd data-testid="detail-email" className="text-right break-all">{a.contact_email || a.email || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Phone</dt><dd data-testid="detail-phone" className="text-right">{a.phone || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">LinkedIn</dt><dd className="text-right break-all">{a.linkedin ? <a data-testid="detail-linkedin" href={a.linkedin.startsWith("http") ? a.linkedin : `https://${a.linkedin}`} target="_blank" rel="noreferrer" className="text-[#026AA2] underline">{a.linkedin}</a> : "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Current workplace</dt><dd data-testid="detail-workplace" className="text-right">{a.current_workplace || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Short bio</dt><dd data-testid="detail-bio" className="text-right">{a.bio || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Degree</dt><dd className="text-right">{a.highest_degree || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Field / Area of study</dt><dd className="text-right">{a.field_of_study || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Professional experience</dt><dd className="text-right">{a.experience_years || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Relevant professional / domain experience</dt><dd className="text-right">{a.relevant_experience || "—"}</dd></div>
                    <div className="flex justify-between py-1.5 gap-4"><dt className="text-[#A3A3A3]">Bengali language background</dt><dd className="text-right">{a.bengali_background || "—"}</dd></div>
                  </dl>
                </div>

                {a.bengali_experience && <div><span className="text-xs font-medium uppercase tracking-wider text-[#525252]">Bengali experience</span><p className="mt-1 text-sm whitespace-pre-wrap">{a.bengali_experience}</p></div>}

                <div><span className="text-xs font-medium uppercase tracking-wider text-[#525252]">Documents</span>
                  <div className="mt-2 space-y-2">
                    {a.resume?.file_id && <button data-testid="detail-resume" onClick={() => downloadFile(a.resume.file_id, a.resume.filename)} className="flex items-center gap-2 text-sm text-[#026AA2] underline"><FileText className="h-4 w-4" /> Resume: {a.resume.filename}</button>}
                    {(a.supporting_documents || []).map((d) => <button key={d.file_id} onClick={() => downloadFile(d.file_id, d.filename)} className="flex items-center gap-2 text-sm text-[#026AA2] underline"><FileText className="h-4 w-4" /> {d.filename}</button>)}
                    {(a.supporting_documents || []).length === 0 && <p className="text-sm text-[#A3A3A3]">No supporting documents.</p>}
                  </div>
                </div>
                {selected.evaluation && <p className="text-sm text-[#525252]">Evaluation: {selected.evaluation.passed ? "Passed" : "Not passed"} · {selected.evaluation.last_score}/100</p>}
              </div>

              <div className="lg:col-span-1 space-y-4">
                <div className="bg-white border border-[#E5E5E5] rounded-sm p-5 space-y-3">
                  <span className="text-xs font-medium uppercase tracking-wider text-[#525252]">Actions</span>
                  <button data-testid="admin-approve-btn" onClick={() => act("approve")} className="w-full bg-[#0A0A0A] text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#262626]">Approve & unlock evaluation</button>
                  <textarea data-testid="admin-info-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Explain what information is required…" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" />
                  <button data-testid="admin-request-info-btn" onClick={() => act("request-info", { message })} className="w-full border border-[#0A0A0A] px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#F5F5F5]">Request more information</button>
                  <button data-testid="admin-not-selected-btn" onClick={() => act("not-selected")} className="w-full border border-[#E5E5E5] text-[#525252] px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#F5F5F5]">Not selected (current pathway)</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
